/*
The following content converts UNIX to Quartz cron expression and it is based on the MIT licensed source: https://github.com/lirantal/cron-to-quartz
*/

export const getQuartz = (crontab: string) => {
  const data = [];

  // check for cron magic entries
  const magicEntry = parseCronMagics(crontab);

  if (magicEntry) {
    data.push(magicEntry);
  } else {
    let quartzEntry = [];

    // if cron magic entries not found, proceed to parsing normal cron format
    const crontabEntry = crontab.split(' ');
    quartzEntry = parseCronSyntax(crontabEntry);

    data.push(quartzEntry);

    // Quartz doesn't support both DOM and DOW definitions so if we find such ocurrence we'll need to
    // create 2 Quartz entries, one with DOM and one with DOW to create an OR expression

    if (crontabEntry[2] !== '*' && crontabEntry[4] !== '*') {
      // by default, parseCronSyntax() gives priority to parse the DOM first so we reset it now to * to
      // make sure we also have a variant of the CRON expression with DOW
      crontabEntry[2] = '*';

      quartzEntry = parseCronSyntax(crontabEntry);
      data.push(quartzEntry);
    }
  }
  return data;
};

const advanceNumber = (str: string) => {
  let quartzCompatibleStr = '';
  let num;
  str.split('').forEach(function (chr) {
    num = parseInt(chr);

    // char is an actual number
    if (!isNaN(num)) {
      // number is in allowed range
      if (num >= 0 && num <= 7) {
        quartzCompatibleStr += num + 1;
      } else {
        // otherwise default to 1, beginning of the week
        quartzCompatibleStr = '1';
      }
    } else {
      quartzCompatibleStr += chr;
    }
  });

  return quartzCompatibleStr;
};

const parseCronSyntax = (crontabEntry: string[]) => {
  const quartzEntry = [];

  // first we initialize the seconds to 0 by default because linux CRON entries do not include a seconds definition
  quartzEntry.push('0');

  // quartz scheduler can't handle an OR definition, and so it doesn't support both DOM and DOW fields to be defined
  // for this reason we need to shift one of them to be the value or * and the other to be ?
  let toggleQuartzCompat = false;

  // if the second if passed, it will remove keeping only the 5 elements
  if (crontabEntry.length === 6) {
    crontabEntry.shift();
  }

  crontabEntry.forEach((item: string, index: number) => {
    // index 0 = minutes
    // index 1 = hours
    // these cron definitions should be compatible with quartz so we push them as is
    if (index === 0 || index === 1) {
      quartzEntry.push(item);
    }

    // index 2 = DOM = Day of Month
    if (index === 2) {
      if (item !== '?') {
        toggleQuartzCompat = true;
      }

      if (item === '*') {
        toggleQuartzCompat = false;
        item = '?';
      }

      quartzEntry.push(item);
    }

    // index 3 = Month
    if (index === 3) {
      quartzEntry.push(item);
    }

    // index 4 = DOW = Day of Week
    if (index === 4) {
      // day of week needs another adjustments - it is specified as 1-7 in quartz but 0-6 in crontab
      const itemAbbreviated = advanceNumber(item);

      if (toggleQuartzCompat === true) {
        quartzEntry.push('?');
      } else {
        quartzEntry.push(itemAbbreviated);
      }
    }

    // beyond index 4 we don't care and exit the loop
    if (index >= 5) {
      return true;
    }
    // this should never happen
    return false;
  });

  // quartz expect a last 7th parameter for scheduling yearly recurrence so we pass * by default for all years
  quartzEntry.push('*');

  return quartzEntry;
};

const parseCronMagics = (crontab: string) => {
  // @hourly
  if (crontab.indexOf('@hourly') === 0) {
    return ['0', '0', '*', '*', '*', '?', '*'];
  }

  // @daily and @midnight
  if (crontab.indexOf('@daily') === 0 || crontab.indexOf('@midnight') === 0) {
    return ['0', '0', '0', '*', '*', '?', '*'];
  }

  // @weekly
  if (crontab.indexOf('@weekly') === 0) {
    return ['0', '0', '0', '?', '*', '1', '*'];
  }

  // @monthly
  if (crontab.indexOf('@monthly') === 0) {
    return ['0', '0', '0', '1', '*', '?', '*'];
  }

  // @yearly and @annually
  if (crontab.indexOf('@yearly') === 0 || crontab.indexOf('@annually') === 0) {
    return ['0', '0', '0', '1', '1', '?', '*'];
  }

  return undefined;
};

/*
The following content converts Quartz into UNIX cron expressions and it is based on the MIT licensed source: https://github.com/rahu619/cron-converter-u2q
*/

class ExpressionHelper {
  static readonly delimiter = ' ';
  static readonly unixExpressionLength = 5;
  static readonly quartzExpressionLengths = [6, 7];

  public static GetExpressionParts(expression: string): string[] | undefined {
    this.validateIfNullOrEmpty(expression);
    const parts = expression.split(this.delimiter).map((part) => part.trim());
    if (this.quartzExpressionLengths.includes(parts.length)) return parts.map((part) => part.replace('?', '*')); //if quartz, cleanup
    if (this.unixExpressionLength === parts.length) return parts; // if unix, return as it is

    console.error(`Invalid cron expression!`);
    return undefined;
  }

  private static validateIfNullOrEmpty(cronExpression: string | undefined | null): void {
    if (!cronExpression || cronExpression.trim() === '') {
      console.error('Empty or null expression');
      return undefined;
    }
  }
}

export class CronConverterU2Q {
  static readonly everyXUnitsReplacePlaceholder = `%s`;
  static readonly quartzEveryXUnitsRegex = /^0\/(\d+)$/; // For handling 0/5 units
  static readonly unixEveryXUnitsRegex = /^\/(\d+)$/; // For handling */5 units
  static readonly quartzEveryXUnitsReplacePattern = `0/${this.everyXUnitsReplacePlaceholder}`;
  static readonly unixEveryXUnitsReplacePattern = `*/${this.everyXUnitsReplacePlaceholder}`;

  /**
   * Converts a quartz cron expression to a unix cron expression
   * @param quartzExpression - the quartz expression
   * @returns the corresponding unix expression
   */
  public static quartzToUnix(quartzExpression: string): string {
    const parts = ExpressionHelper.GetExpressionParts(quartzExpression);
    if (!parts) return '';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, min, hour, dom, month, dow] = parts.map((part) => this.convertIntervalParts(part, true));

    // Converting Quartz DOW to Unix DOW
    const unixDow = dow?.includes(',')
      ? dow
          .split(',')
          .map((day) => {
            if (day === '1') return '0';
            if (day === '?') return '*';
            return (parseInt(day, 10) - 1).toString();
          })
          .join(',')
      : dow;

    let unixDom = dom;

    // If dow in Quartz was '?', set unixDom to '*'
    if (dow === '?') unixDom = '*';

    return `${min} ${hour} ${unixDom} ${month} ${unixDow}`;
  }

  private static convertIntervalParts(part: string, isQuartz = false): string {
    const everyXUnitsPattern = isQuartz ? this.quartzEveryXUnitsRegex : this.unixEveryXUnitsRegex;
    const matches = part.match(everyXUnitsPattern);
    const everyXUnitsReplacePattern = isQuartz
      ? this.quartzEveryXUnitsReplacePattern
      : this.unixEveryXUnitsReplacePattern;

    if (matches) return `${everyXUnitsReplacePattern.replace(this.everyXUnitsReplacePlaceholder, matches[1])}`;

    return part;
  }
}
